import { useMemo, useState } from 'react'
import type { Product, ResolvedAddonItem } from '@/lib/types'
import { fmt } from '@/lib/utils'
import { useKioskStore } from '@/stores/kiosk-store'

// Upsell-modal som visas efter "Lägg i varukorg" om produkten + menytypen
// har kopplade addons. Visar ETT steg åt gången. Kund klickar på items för
// att lägga till, + / − på markerade items för antal. "Nej tack / Fortsätt"
// går till nästa steg (eller stänger flödet).

export default function AddonStepModal() {
  const flow = useKioskStore((s) => s.addonFlow)
  if (!flow) return null
  const step = flow.steps[flow.currentIdx]
  if (!step) return null
  return <AddonStepInner key={step.id + flow.currentIdx} stepId={step.id} />
}

function AddonStepInner({ stepId }: { stepId: string }) {
  const flow = useKioskStore((s) => s.addonFlow)
  const pages = useKioskStore((s) => s.pages)
  const locale = useKioskStore((s) => s.locale)
  const addToAddonFlowSelection = useKioskStore((s) => s.addToAddonFlowSelection)
  const advanceAddonFlow = useKioskStore((s) => s.advanceAddonFlow)
  const cancelAddonFlow = useKioskStore((s) => s.cancelAddonFlow)

  // Quantities per menuProductId för detta steg
  const [qty, setQty] = useState<Record<string, number>>({})

  const step = flow?.steps[flow.currentIdx]
  const totalSteps = flow?.steps.length ?? 0
  const stepNumber = (flow?.currentIdx ?? 0) + 1

  // Lookup: menuProductId → Product (från laddade menyn)
  const productLookup = useMemo(() => {
    const map = new Map<string, Product>()
    for (const page of pages) {
      for (const cat of page.categories) {
        for (const p of cat.products) map.set(p.id, p)
      }
    }
    return map
  }, [pages])

  if (!flow || !step || step.id !== stepId) return null

  const sv = locale === 'sv'

  // Default TRUE for addons (t.ex. 2x extra ost rimligt). Om false → varje
  // item kan bara väljas en gang, klick på kortet togglar, ingen +/-
  // räknare visas.
  const allowDuplicates = step?.allowDuplicates ?? true

  function adjust(menuProductId: string, delta: number) {
    setQty((prev) => {
      const next = (prev[menuProductId] || 0) + delta
      if (next <= 0) {
        const { [menuProductId]: _drop, ...rest } = prev
        void _drop
        return rest
      }
      return { ...prev, [menuProductId]: next }
    })
  }

  // Toggle-variant for allowDuplicates=false. Klick togglar selected (0 eller 1).
  function toggleSelect(menuProductId: string) {
    setQty((prev) => {
      if (prev[menuProductId]) {
        const { [menuProductId]: _drop, ...rest } = prev
        void _drop
        return rest
      }
      return { ...prev, [menuProductId]: 1 }
    })
  }

  function handleContinue() {
    if (step == null) return
    // Resolvera de valda items. Vi bär både override (price — vad kund ser)
    // och menypriset (menuPrice) framåt. discountAmount räknas vid payment.
    const items: ResolvedAddonItem[] = []
    for (const [menuProductId, count] of Object.entries(qty)) {
      if (count <= 0) continue
      const product = productLookup.get(menuProductId)
      if (!product?.properties) continue
      const addonItem = step?.items.find((it) => it.menuProductId === menuProductId)
      const menuPrice = product.properties.price || 0
      const override = addonItem?.priceOverride != null ? addonItem.priceOverride : menuPrice
      items.push({
        addonDefId: step.id,
        productId: product.properties.id,
        productStateId: product.properties.stateId,
        name: product.name,
        price: override,
        menuPrice,
        imageUrl: product.properties.imageUrl,
        count,
      })
    }
    if (items.length > 0) addToAddonFlowSelection(items)
    advanceAddonFlow()
  }

  const anySelected = Object.values(qty).some((n) => n > 0)

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center kiosk-fade-in">
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative bg-kiosk-surface w-[92%] max-w-3xl max-h-[90vh] flex flex-col rounded-3xl z-10 kiosk-slide-up overflow-hidden">
        {/* Header. Engelsk titel kommer från admin-redigerad step.titleEn;
            faller tillbaka till svensk step.title om inte satt. */}
        <div className="px-6 pt-6 pb-4 border-b border-kiosk-surface-2 relative">
          <h3 className="text-kiosk-text text-3xl font-bold text-center">{locale === 'en' ? step.titleEn || step.title || step.name : step.title || step.name}</h3>
          {totalSteps > 1 && <span className="absolute top-6 right-6 text-kiosk-muted text-xs shrink-0">{sv ? `${stepNumber}/${totalSteps}` : `${stepNumber}/${totalSteps}`}</span>}
        </div>

        {/* Items — layout styrs av addon-def: list (rad med vänsterbild) eller grid2/grid3 (kort med bild på topp) */}
        <div className={`overflow-y-auto kiosk-scroll flex-1 p-4 ${step.layout === 'grid2' ? 'grid grid-cols-2 gap-3' : step.layout === 'grid3' ? 'grid grid-cols-3 gap-3' : 'space-y-2'}`}>
          {step.items.length === 0 ? (
            <p className="text-kiosk-muted text-center py-8 col-span-full">{sv ? 'Inga alternativ i det här steget.' : 'No options in this step.'}</p>
          ) : (
            step.items.map((it, i) => {
              const product = productLookup.get(it.menuProductId)
              if (!product) return null
              const count = qty[it.menuProductId] || 0
              const selected = count > 0
              // Visa override-pris om satt, annars menypris
              const price = it.priceOverride != null ? it.priceOverride : product.properties?.price || 0
              const imageUrl = product.properties?.imageUrl
              const isGrid = step.layout === 'grid2' || step.layout === 'grid3'
              const productName = product.name

              if (isGrid) {
                // Tile-layout: bild på topp, namn+pris under (som kategorigriden)
                return (
                  <div
                    key={i}
                    onClick={() => (allowDuplicates ? adjust(it.menuProductId, selected ? 0 : 1) : toggleSelect(it.menuProductId))}
                    className={`relative bg-kiosk-bg rounded-2xl overflow-hidden cursor-pointer transition-all ring-2 shadow-lg shadow-black/40 ${
                      selected ? 'ring-kiosk-accent' : 'ring-white/10 hover:ring-white/30'
                    }`}
                  >
                    {/* Bild */}
                    <div className="aspect-4/3 w-full bg-kiosk-surface-2 overflow-hidden">
                      {imageUrl ? (
                        <img src={imageUrl} alt={productName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-kiosk-muted text-4xl">🍽</div>
                      )}
                    </div>
                    {/* Selected-check uppe till höger */}
                    {selected && (
                      <div className="absolute top-2 right-2 w-9 h-9 rounded-full bg-kiosk-accent flex items-center justify-center shadow-lg">
                        <svg className="w-5 h-5 text-kiosk-bg" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                    {/* Namn + pris */}
                    <div className="p-3">
                      <div className="text-kiosk-text text-sm font-semibold truncate">{productName}</div>
                      {price > 0 && <div className="text-kiosk-accent text-base font-bold mt-0.5">+{fmt(price)}</div>}
                      {price === 0 && <div className="text-green-400 text-sm mt-0.5">{sv ? 'Gratis' : 'Free'}</div>}
                    </div>
                    {/* Räknare — nedre högra när selected, bara om duplikater tillaten */}
                    {selected && allowDuplicates && (
                      <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-kiosk-surface/95 rounded-full p-1 backdrop-blur" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => adjust(it.menuProductId, -1)} className="kiosk-btn w-8 h-8 rounded-full bg-kiosk-bg text-kiosk-text flex items-center justify-center text-lg font-bold">
                          &minus;
                        </button>
                        <span className="text-kiosk-text text-base w-5 text-center">{count}</span>
                        <button onClick={() => adjust(it.menuProductId, 1)} className="kiosk-btn w-8 h-8 rounded-full bg-kiosk-accent text-kiosk-bg flex items-center justify-center text-lg font-bold">
                          +
                        </button>
                      </div>
                    )}
                  </div>
                )
              }

              // Default: list-layout (rad med vänsterbild)
              return (
                <div
                  key={i}
                  onClick={() => (allowDuplicates ? adjust(it.menuProductId, selected ? 0 : 1) : toggleSelect(it.menuProductId))}
                  className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all ${
                    selected ? 'bg-kiosk-surface-2 ring-1 ring-kiosk-accent/40' : 'bg-kiosk-bg hover:bg-kiosk-surface-2/60'
                  }`}
                >
                  {/* Checkbox */}
                  <div className={`w-7 h-7 shrink-0 rounded-lg border-2 flex items-center justify-center ${selected ? 'border-kiosk-accent bg-kiosk-accent' : 'border-kiosk-muted/40'}`}>
                    {selected && (
                      <svg className="w-4 h-4 text-kiosk-bg" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  {/* Bild */}
                  {imageUrl && <img src={imageUrl} alt={productName} className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                  {/* Namn */}
                  <div className="flex-1 min-w-0">
                    <span className="text-kiosk-text text-base font-medium block truncate">{productName}</span>
                    {price > 0 && <span className="text-kiosk-accent text-sm">+{fmt(price)}</span>}
                    {price === 0 && <span className="text-green-400 text-sm">{sv ? 'Gratis' : 'Free'}</span>}
                  </div>
                  {/* Räknare — bara om selected OCH duplikater tillaten */}
                  {selected && allowDuplicates && (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => adjust(it.menuProductId, -1)} className="kiosk-btn w-10 h-10 rounded-full bg-kiosk-bg text-kiosk-text flex items-center justify-center text-xl font-bold">
                        &minus;
                      </button>
                      <span className="text-kiosk-text text-lg w-6 text-center">{count}</span>
                      <button onClick={() => adjust(it.menuProductId, 1)} className="kiosk-btn w-10 h-10 rounded-full bg-kiosk-accent text-kiosk-bg flex items-center justify-center text-xl font-bold">
                        +
                      </button>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>

        {/* Fotnot: Nej tack + Fortsätt */}
        <div className="px-6 py-4 border-t border-kiosk-surface-2 space-y-2">
          <button onClick={handleContinue} className="kiosk-btn w-full py-4 bg-kiosk-accent text-kiosk-bg font-bold rounded-xl text-lg">
            {anySelected ? (sv ? 'Lägg till & fortsätt' : 'Add & continue') : sv ? 'Nej tack, fortsätt' : 'No thanks, continue'}
          </button>
          <button onClick={cancelAddonFlow} className="kiosk-btn w-full py-2 text-kiosk-muted text-sm">
            {sv ? 'Avbryt' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  )
}
